import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star } from "lucide-react";
import { useUserFeedback } from "@/hooks/useUserFeedback";

interface ModalNoterSiteProps {
  open: boolean;
  onClose: () => void;
}

export default function ModalNoterSite({ open, onClose }: ModalNoterSiteProps) {
  const [note, setNote] = useState<number>(0);
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const { sendFeedback, loading, error, success } = useUserFeedback();
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await sendFeedback({
      feedbackType: "note",
      note,
      commentaire: comment,
    });
  }

  // Gérer la transition success → sent local avec message + auto reset sur 3s ou plus
  useEffect(() => {
    if (success) {
      setSent(true);
      const timeout = setTimeout(() => {
        setSent(false);
        setNote(0);
        setComment("");
      }, 5000); // message visible 3 secondes

      return () => clearTimeout(timeout);
    }
  }, [success]);

  // Reset local dès ouverture pour pouvoir refaire la notation
  useEffect(() => {
    if (open) {
      setSent(false);
      setNote(0);
      setComment("");
    }
  }, [open,onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="noter-modal-bg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.14)",
            zIndex: 10001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={onClose}
        >
          <motion.div
            key="noter-modal-card"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            style={{
              background: "var(--background, #fff)",
              borderRadius: 12,
              padding: 26,
              minWidth: 320,
              boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
              position: "relative",
              maxWidth: "92vw",
              width: 370,
              zIndex: 10002,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "none",
                position: "absolute",
                top: 12,
                right: 14,
                cursor: "pointer",
                padding: 3,
                borderRadius: 5,
                color: "var(--muted, #666)",
              }}
              aria-label="Fermer"
            >
              <X size={22} />
            </button>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <Star size={26} color="#f7c600" />
              <h2 style={{ fontWeight: 600, fontSize: "1.14rem", margin: 0 }}>Noter le site</h2>
            </div>

            {!sent ? (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 10 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 5 }}>Votre note</div>
                  <div style={{ display: "flex", gap: 3 }}>
                    {Array.from({ length: 5 }, (_, i) => i + 1).map((star) => (
                      <Star
                        key={star}
                        size={26}
                        color={(hover ?? note) >= star ? "#f7c600" : "#bbb"}
                        style={{ cursor: "pointer", transition: "color 0.15s" }}
                        fill={(hover ?? note) >= star ? "#f7c600" : "none"}
                        onMouseEnter={() => setHover(star)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => setNote(star)}
                        aria-label={`${star} étoile${star > 1 ? "s" : ""}`}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="avis" style={{ fontWeight: 500, fontSize: 13 }}>
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    id="avis"
                    maxLength={500}
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Votre avis ou suggestion…"
                    style={{
                      width: "100%",
                      resize: "vertical",
                      padding: "7px",
                      borderRadius: 4,
                      border: "1px solid #ddd",
                      marginTop: 3,
                    }}
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || note === 0}
                  style={{
                    background: "#f7c600",
                    color: "#232323",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 7,
                    padding: "10px 0",
                    fontSize: "1rem",
                    marginTop: 4,
                    cursor: loading ? "wait" : note === 0 ? "not-allowed" : "pointer",
                    opacity: note === 0 ? 0.66 : 1,
                    transition: "background 0.19s",
                  }}
                >
                  {loading ? "Envoi…" : "Envoyer mon avis"}
                </button>
                {error && (
                  <div style={{ color: "red", fontWeight: "600", marginTop: 10 }}>
                    {error}
                  </div>
                )}
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ color: "green", marginTop: 20, textAlign: "center" }}
              >
                Merci pour votre retour !
                <br />
                Votre avis a bien été transmis.
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

